import { useState } from 'react';

import type { Environment } from '@change-corgi/core/environment';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

import { useChatAgentSubmit } from '../useChatAgentSubmit';

type UseChatAgentReturn = ModelHookResult<
	{ iframeSrc: string; loading: boolean },
	{ onSubmit: ReturnType<typeof useChatAgentSubmit>['actions']['onSubmit'] }
>;

/** Currently, only production vs. non-production matters for the chat agent's own deployment. */
function getChildOrigin(environment: Environment): string {
	if (environment === 'development') return 'https://petition-chat.localhost';

	// TODO: replace with the real petition-chat origin(s) once available.
	if (environment === 'production') return 'https://petition-chat.change.org';
	return 'https://petition-chat.staging-change.org';
}

export function useChatAgent(): UseChatAgentReturn {
	const { environment } = useUtilityContext();
	const { data } = useStartAPetitionPageState();

	// Captured once on mount: `data.draft` can keep syncing while the chat is open (e.g. the sandbox's
	// `update` messages), and recomputing `src` from live draft state would change the iframe's `src`
	// prop, which makes React reload the document and drop the in-progress conversation.
	const [iframeSrc] = useState(() => {
		const childOrigin = getChildOrigin(environment.getEnvironment());
		const url = new URL('/chat', childOrigin);

		const issuePrompt = data.draft?.input?.issuePrompt;
		if (issuePrompt) url.searchParams.set('initial', issuePrompt);

		return url.toString();
	});

	const { data: submitData, actions: submitActions } = useChatAgentSubmit();

	return {
		data: { iframeSrc, loading: submitData.loading },
		actions: { onSubmit: submitActions.onSubmit },
	};
}
